// src/components/clients/InlineCell.jsx
import React, { useEffect, useRef, useState } from "react";

/**
 * InlineCell
 * In-row editable cell with optimistic UX.
 *
 * Props:
 *   value        : string | number
 *   type         : 'text' | 'email' | 'tel' | 'select'
 *   options      : [{label, value}] (when type='select')
 *   onSave       : (nextValue) => Promise<void> | void
 *   validate     : (val) => string | null   // return error message or null
 *   className    : string
 *   disabled     : boolean
 */
export default function InlineCell({
  value,
  type = "text",
  options = [],
  onSave,
  validate,
  className = "",
  disabled = false,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => setDraft(value ?? ""), [value]);
  useEffect(() => {
    if (editing) setTimeout(() => inputRef.current?.focus(), 0);
  }, [editing]);

  const start = () => {
    if (disabled) return;
    setEditing(true);
    setErr(null);
  };

  const cancel = () => {
    setEditing(false);
    setDraft(value ?? "");
    setErr(null);
  };

  const commit = async () => {
    const trimmed = typeof draft === "string" ? draft.trim() : draft;
    const msg = validate ? validate(trimmed) : null;
    if (msg) {
      setErr(msg);
      return;
    }
    if (trimmed === value) {
      setEditing(false);
      return;
    }
    try {
      setBusy(true);
      await onSave?.(trimmed);
      setEditing(false);
      setErr(null);
    } catch (e) {
      setErr(e?.message || "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  if (!editing) {
    return (
      <button
        type="button"
        className={`inline-flex max-w-full items-center gap-2 text-left hover:underline ${disabled ? "cursor-not-allowed text-gray-400" : "text-blue-700"} ${className}`}
        onClick={start}
        title={disabled ? "" : "Click to edit"}
      >
        <span className="truncate text-gray-800 hover:text-blue-800">
          {value || "—"}
        </span>
      </button>
    );
  }

  const commonProps = {
    ref: inputRef,
    value: draft,
    onChange: (e) => setDraft(e.target.value),
    onBlur: commit,
    onKeyDown: (e) => {
      if (e.key === "Enter") commit();
      if (e.key === "Escape") cancel();
    },
    className:
      "w-full rounded border px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200",
    disabled: busy,
  };

  return (
    <div className={`min-w-[160px] ${className}`}>
      {type === "select" ? (
        <select {...commonProps}>
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      ) : (
        <input type={type} {...commonProps} />
      )}
      {err ? <div className="mt-1 text-xs text-rose-600">{err}</div> : null}
    </div>
  );
}
